import * as React from "react";
import { observer } from "mobx-react";
import lighten from "polished/lib/color/lighten";
import styled from "styled-components";
import {
	Input,
	Button,
	Radio,
	ButtonGroup,
	Checkbox,
	Tooltip,
	TooltipText,
} from "@lib/components";
import { Store } from "@lib/components/src/components/table/pro/store";
import { DropWrapper, DropSection, DropLRContent } from "@lib/components/src/components/table/pro/components";
import {
	BookingsDateRangeFilter,
	BookingDatePreset,
} from "./bookings-date-range-filter";
import { BookingsTableStore } from "./bookings-protable-store";

interface Props<T> {
	store: Store<T>;
	query: T.Lib.Table.Query;
	region: T.Schema.Restaurant.RestaurantSchema["settings"]["region"];
}

const StyledTooltip = styled((props) => <Tooltip {...props} />)`
	${TooltipText} {
		background-color: #fff;
		color: #383838;
		border: 1px solid ${(props) => lighten(0.2, props.theme.colors.primary)};
		text-align: left;
		padding: 11px 10px;
		border-radius: 8px;
		margin-top: 10px;
		::after {
			top: 50%;
			left: 100%;
			margin-top: -5px;
			border-color: transparent transparent transparent #fff;
		}
	}
`;

@observer
export class BookingsProtableFilters<T> extends React.Component<Props<T>> {
	componentDidMount = () => {
		this.initialAction();
	};

	initialAction = () => {
		for (const filter of this.props.query.filters) {
			if (filter.defaultValues) {
				for (const defaultValue of filter.defaultValues) {
					this.props.store.filterToggle(
						filter.key,
						defaultValue as string
					);
				}
			}
		}
	};

	applyPreset = (
		preset: BookingDatePreset,
		range?: { start: number; end: number }
	) => {
		const store = this.props.store as BookingsTableStore<T>;
		store.setDatePreset(preset);

		if (preset === "all") {
			store.setDateRange(undefined, undefined);
		} else if (range) {
			store.setDateRange(range.start, range.end);
		}

		store.applySortForDatePreset(preset);
		store.fetch();
	};

	handleReset = () => {
		this.props.store.reset();
		this.initialAction();
		this.props.store.fetch();
	};

	render() {
		const { query, store, region } = this.props;
		const s = store.s;
		const bookingsStore = store as BookingsTableStore<T>;

		return (
			<DropWrapper active={s.dropdown === "filters"}>
				<DropSection>
					<DropLRContent>
						<p className="font-semi-bold">Search</p>
						<Input
							value={s.search}
							onChange={(e: any) =>
								store.update({ search: e.target.value })
							}
							placeholder="Search by name, email, phone..."
						/>
					</DropLRContent>
				</DropSection>

				<DropSection>
					<DropLRContent>
						<p className="font-semi-bold">Booking Date</p>
						<BookingsDateRangeFilter
							region={region}
							preset={bookingsStore.datePreset}
							start={s.fromDate}
							end={s.toDate}
							onPresetChange={this.applyPreset}
							onCustomRangeChange={(start, end) => {
								bookingsStore.setDatePreset("custom");
								bookingsStore.setDateRange(start, end);
							}}
						/>
					</DropLRContent>
				</DropSection>

				<DropSection>
					<DropLRContent>
						<p className="font-semi-bold">Sort By</p>
						<div className="flex-line flex-wrap child-mr-15">
							{query.sortKeys.map((sort, i) => {
								const name = `${sort.value}-sort-key`;
								return (
									<div key={i} className="p-tb-1">
										<Radio
											id={name}
											name={name}
											checked={s.sortKey === sort.value}
											title={sort.label}
											value={sort.value as string}
											onChange={() => {
												store.update({
													sortKey: sort.value as string,
												});
											}}
										/>
									</div>
								);
							})}
						</div>
					</DropLRContent>
				</DropSection>

				<DropSection>
					<DropLRContent>
						<p className="font-semi-bold">Sort Direction</p>
						<div>
							<ButtonGroup
								width={100}
								size="xs"
								selected={s.sortDirection}
								options={[
									{ value: -1, name: "Descending" },
									{ value: 1, name: "Ascending" },
								]}
								onSelect={(v: any) =>
									store.update({
										sortDirection: v.value as 1 | -1,
									})
								}
							/>
						</div>
					</DropLRContent>
				</DropSection>

				{query.filters.map((filter, indexFilter) => (
					<DropSection key={indexFilter}>
						<DropLRContent>
							<div className="font-semi-bold">{filter.label}</div>
							<div className="flex-line flex-wrap child-mr-15">
								{filter.values.map((value, indexValue) => {
									const name = `${value.value}-filter-key-${indexValue}`;
									return (
										<div key={indexValue} className="p-tb-1">
											<StyledTooltip
												text={value.tooltip}
												width={200}
												position={"bottom"}
												disable={!value.tooltip}
											>
												<Checkbox
													id={name}
													name={name}
													checked={store.filterIsActive(
														filter.key,
														value.value as string
													)}
													label={value.label}
													onChange={() =>
														store.filterToggle(
															filter.key,
															value.value as string
														)
													}
												/>
											</StyledTooltip>
										</div>
									);
								})}
							</div>
						</DropLRContent>
					</DropSection>
				))}

				<DropSection>
					<Button
						color="primary"
						size="xs"
						onClick={store.fetch}
						className="m-r-2"
					>
						Search
					</Button>
					<Button
						color="primary-inverse"
						size="xs"
						onClick={this.handleReset}
					>
						Reset
					</Button>
				</DropSection>
			</DropWrapper>
		);
	}
}
